import { useTranslation } from 'react-i18next';

import { ErrorResponseType, IdentityVerificationResponseDto } from '@repo/services/hooks';
import { IdentifyVerificationBody } from '@repo/ui/overlay';
import { useQueryClient } from '@tanstack/react-query';

import { getPortOneConfig } from 'constant/portOne';
import { useCreateUsersIdentityVerification } from 'services/hooks/verification';
import { useModalStackStore } from 'store/modalStackStore';
import { LOADING_KEY } from 'utils/promise';

import { useCreateIdentityVerificationWithRedirect } from 'pages/dashboard/hooks/useCreateIdentityVerificationWithRedirect';

import VerificationButton, { BaseVerificationProps } from '../shared/VerificationButton';

type IdentityVerificationButtonProps = {
  identityData?: IdentityVerificationResponseDto;
} & BaseVerificationProps;

export const IdentityVerificationButton = ({
  identityData,
  isVerificationCompleted,
}: IdentityVerificationButtonProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModalStackStore();
  const { mutateAsync: createUsersIdentityVerification } = useCreateUsersIdentityVerification();

  // 포트원에서 인증된 트랜잭션을 서버에 요청하기 위한 함수
  const postIdentityVerificationTransaction = async (identityVerificationId: string) => {
    createUsersIdentityVerification(identityVerificationId, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['identityVerification'] });
        closeModal();
      },
      onError: (error) => {
        const {
          response: {
            data: { message, code },
          },
        } = error as unknown as ErrorResponseType;

        return alert(code === 'VERIFICATION_LIMIT_EXCEEDED' ? t('chat.profile_my_already_verified') : message[0]);
      },
    });
  };

  // 모바일에서 인증 시, RedirectUrl로 처리(모바일 본인 인증 대응 로직)
  useCreateIdentityVerificationWithRedirect(postIdentityVerificationTransaction);

  const handleOpenIdentityVerificationModal = () => {
    openModal(
      <IdentifyVerificationBody
        identityData={identityData}
        postIdentityVerificationTransaction={postIdentityVerificationTransaction}
        portOneConfig={getPortOneConfig(window.location.href)}
        closeModal={closeModal}
      />,
      {
        closeOnBackdropClick: false,
      }
    );
  };

  return (
    <>
      <VerificationButton
        isDisabled={false}
        isCompleted={isVerificationCompleted}
        onClick={handleOpenIdentityVerificationModal}
        loadingKey={LOADING_KEY.ID_VERIFICATIONS}
      />
    </>
  );
};
